// src/components/Header.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { startRouteForUser } from "../utils/startRouteForUser";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import { Sparkles } from "lucide-react";
import { getProfilePathByContext } from "@/utils/getProfilePathByContext";
import { openVision } from "../utils/openVision";
import { BRAND_NAME, BRAND_ENDORSEMENT, BRAND_LOGO_VARIANT } from "../config/brand";
import artichautMonogramme from "../assets/brand/artichaut-monogramme.svg";

export const Header = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, globalSignOut, user, profile, loading, profileLoading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [hasAdvisor, setHasAdvisor] = useState(false);
  const location = useLocation();

  // Le collectionneur relié à un dossier d'Art Advisor voit un lien « Mon conseiller ».
  // has_active_advisor() couvre les deux mécanismes de liaison qui coexistent :
  // advisor_client_entities (flux récent §8bis) ET les accès collection_permissions
  // accordés directement à un profil plan art_advisor/art_advisor_premium (flux plus
  // ancien, jamais rétro-rempli dans advisor_client_entities — cf. Marie Dubois /
  // Pierre Martin / Fondation Champ Libre liés à Sophie Delorme sans ligne dédiée).
  useEffect(() => {
    let alive = true;
    if (!user?.id) { setHasAdvisor(false); return; }
    (async () => {
      const { data } = await supabase.rpc("has_active_advisor");
      if (alive) setHasAdvisor(!!data);
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const profilePath = getProfilePathByContext(location.pathname, profile);
  const isAdmin = user?.role === "admin" || user?.is_admin === true;
  
  // Hint "pro" par URL (evite flash collector quand profile pas encore charge)
  const pathname = location?.pathname || "";
  const isExpertRoute = pathname.startsWith("/expert");
  const isPartnerRoute = pathname.startsWith("/partner");
  const isAdminRoute = pathname.startsWith("/admin");
  const isFamilyOfficeRoute = pathname.startsWith("/family-office");
  const profileReady = !!profile;

  // ✅ Mémoriser le role une fois qu'on le connait (évite la perte lors des re-renders)
  const [persistedRole, setPersistedRole] = useState(() => {
    try {
      return localStorage.getItem('user_role') || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (profile?.role) {
      try {
        localStorage.setItem('user_role', profile.role);
        setPersistedRole(profile.role);
      } catch {}
    }
  }, [profile?.role]);

  // ✅ Cleanup au logout
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        localStorage.removeItem('user_role');
        setPersistedRole(null);
      } catch {}
    }
  }, [isAuthenticated]);

  // PRIORITE URL > profile.role > persistedRole (securite navigation)
  // Si on est sur /expert/*, on force role="expert" meme si profile.role est temporairement null
  const roleFallback =
    isExpertRoute ? "expert" :
    isPartnerRoute ? "partner" :
    isAdminRoute ? "admin" :
    isFamilyOfficeRoute ? "family_office" :
    profile?.role ||
    persistedRole ||
    user?.user_metadata?.role ||
    user?.app_metadata?.role ||
    "user";

  const isAdminTeamFallback = roleFallback === "admin" || roleFallback === "team";
  const isExpertFallback = roleFallback === "expert";
  const isFamilyOfficeFallback = roleFallback === "family_office";
  const isPartnerFallback =
    roleFallback === "partner" ||
    roleFallback === "partner_insurance" ||
    roleFallback === "partner_transport";
  const isPro =
    isAdminTeamFallback ||
    isExpertFallback ||
    isPartnerFallback ||
    isFamilyOfficeFallback ||
    isExpertRoute ||
    isPartnerRoute ||
    isAdminRoute;

  // Collaborateur délégué : route /delegated OU flag localStorage posé par DelegatedAccessDashboard
  const isCollaboratorRoute = pathname.startsWith("/delegated");
  const hasDelegatedContext = (() => {
    try { return !!localStorage.getItem("delegated_context"); } catch { return false; }
  })();
  const isCollaborator = !isPro && (isCollaboratorRoute || hasDelegatedContext);

  const accountMode = profile?.account_mode || "full";
  const isDelegated = !isPro && (accountMode === "delegated" || hasDelegatedContext);

  // Pro => jamais delegated_context (sinon tu "perds le role" en changeant de page)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isPro) return;
    try {
      localStorage.removeItem("delegated_context");
    } catch (_) {}
  }, [isAuthenticated, isPro]);

// Destination de secours pendant le chargement du profil.
// Elle respecte l'espace détecté par l'URL ou le rôle mémorisé.
const fallbackDashboardPath =
  isFamilyOfficeFallback
    ? "/family-office/dashboard"
    : isExpertFallback
    ? "/expert/dashboard"
    : isAdminTeamFallback
    ? roleFallback === "team"
      ? "/team/dashboard"
      : "/admin/dashboard"
    : isPartnerFallback
    ? roleFallback === "partner_transport"
      ? "/partner/transport"
      : roleFallback === "partner_insurance"
      ? "/partner/insurance"
      : "/partner"
    : "/dashboard";

const logoDestination = !isAuthenticated
  ? "/"
  : isDelegated
  ? "/delegated"
  : profileReady
  ? startRouteForUser(profile)
  : fallbackDashboardPath;
console.log("[Header] roleFallback", roleFallback, "profileReady", profileReady);


// Utilise par les liens "Tableau de bord" (desktop + mobile)
const proDashboardPath = profileReady
  ? startRouteForUser(profile)
  : fallbackDashboardPath;

// ✅ Routes pro spécifiques
const proCollectionsPath = isExpertFallback ? "/expert/collections" : null;

  const closeMobileMenu = () => {
    if (!isMenuOpen) return;
    // animation 200ms puis suppression du DOM
    setIsMenuClosing(true);
    window.setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
    }, 200);
  };

  const toggleMobileMenu = () => {
    if (isMenuOpen) {
      closeMobileMenu();
    } else {
      setIsMenuOpen(true);
      setIsMenuClosing(false);
    }
  };

   const handleLogout = async () => {
    try {
      closeMobileMenu();
      // Deconnexion cross-domain via AuthContext (Vision + local)
      if (globalSignOut) {
        await globalSignOut();
      }
      
      // hard redirect: evite tout etat React "entre deux" apres SIGNED_OUT
      window.location.assign("/");
    } catch (error) {
      console.error("Erreur lors de la deconnexion:", error);
    }
  };



  // Petit selecteur inline pour le menu mobile (utilise i18n directement)
const lang = (i18n?.language || "fr");
  const MobileLangSelector = () => (

    <div className="px-4 py-2">
<label className="block text-xs text-gray-500 mb-2">
  {lang.startsWith("fr") ? "Langue" : "Language"}
</label>

<select
  value={lang.startsWith("en") ? "en" : "fr"}
        onChange={(e) => {
          i18n.changeLanguage(e.target.value);
          localStorage.setItem("i18nextLng", e.target.value);
        }}
        className="w-full border border-gray-300 px-3 py-2 text-sm"
      >
        <option value="fr">Français</option>
        <option value="en">English</option>
      </select>
    </div>
  );
  
  // Evite le "flash" collector tant qu'on ne sait pas (profil en cours)
  if (isAuthenticated && (loading || profileLoading)) {
    return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-20">
                <>
                  <Link
                    to={proDashboardPath}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                  >
                    {t("header.nav.dashboard")}
                  </Link>

                  {/* ✅ Expert: accès direct aux collections partagées */}
                  {proCollectionsPath && (
                    <Link
                      to={proCollectionsPath}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                    >
                      {t("header.nav.collection")}
                    </Link>
                  )}
                </>
            <div className="hidden lg:flex items-center space-x-3">
              <LanguageSelector />
              <button
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm font-medium bg-gray-100 text-gray-900 hover:bg-gray-200 rounded-lg transition-all"
              >
                {t("header.logout")}
              </button>
            </div>
          </div>
        </div>
      </header>
    );
  }
  // ── Header minimaliste pour les collaborateurs délégués ──────────────────
  // S'affiche sur TOUTES les routes (y compris /artwork/:id) tant que
  // le flag delegated_context est présent dans localStorage.
  if (isAuthenticated && isCollaborator) {
    return (
      <header style={{
        background: "#fff", borderBottom: "1px solid #e5e3df",
        position: "sticky", top: 0, zIndex: 50,
        padding: "0 32px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <Link to="/delegated" style={{
            fontFamily: "Georgia,serif", fontSize: 17, color: "#1a1a1a",
            letterSpacing: "0.1em", textDecoration: "none",
          }}>
            {BRAND_NAME}
          </Link>
          <Link to="/delegated" style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 13, color: "#6b6b6b", textDecoration: "none",
            borderLeft: "1px solid #e5e3df", paddingLeft: 16,
          }}>
            ← Mon espace
          </Link>
        </div>
        <button
          onClick={handleLogout}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 13, color: "#6b6b6b", background: "none",
            border: "none", cursor: "pointer",
          }}
        >
          Déconnexion
        </button>
      </header>
    );
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to={logoDestination} className="flex items-center space-x-2 group">
            {BRAND_LOGO_VARIANT === "artichaut" ? (
              <img
                src={artichautMonogramme}
                alt=""
                aria-hidden="true"
                className="w-8 h-8 transition-transform group-hover:scale-105"
              />
            ) : (
              <div
                className="w-10 h-10 bg-[#0A0A0A] rounded-lg flex items-center justify-center transition-transform group-hover:scale-105"
                aria-hidden="true"
              >
                {/* Symbole ARTKIV : cadre ouvert, indépendant du nom de marque */}
                <svg viewBox="0 0 40 40" className="w-7 h-7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16V9H16" stroke="#F6F2E2" strokeWidth="3" />
                  <path d="M24 9H31V16" stroke="#F6F2E2" strokeWidth="3" />
                  <path d="M31 24V31H24" stroke="#F6F2E2" strokeWidth="3" />
                  <path d="M16 31H9V24" stroke="#F6F2E2" strokeWidth="3" />
                </svg>
              </div>
            )}
            <span className="flex items-baseline gap-2">
              <span className="text-2xl font-serif font-bold tracking-tight">{BRAND_NAME}</span>
              {BRAND_ENDORSEMENT && (
                <span className="text-xs text-gray-400 tracking-wide">{BRAND_ENDORSEMENT}</span>
              )}
            </span>
          </Link>

          {/* Desktop Nav */}
          {isAuthenticated && (
            <nav className="hidden lg:flex items-center space-x-1">
              {!isPro ? (
                <>
                  <Link
                    to="/collections"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                  >
                    {t("header.nav.collection")}
                  </Link>
                  <Link
                    to="/estimation"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                  >
                    {t("header.nav.estimates")}
                  </Link>
                  <Link
                    to="/commencer"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                  >
                    {t("header.nav.start", "Commencer")}
                  </Link>
                  {hasAdvisor && (
                    <Link
                      to="/mon-conseiller"
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                    >
                      {t("header.nav.myAdvisor", "Mon conseiller")}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => openVision()}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all flex items-center gap-1.5"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("header.nav.analysesIA", "Analyses IA")}
                  </button>
                  <div
                    className="relative"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all flex items-center gap-1">
                      {t("header.nav.services")}
                      <svg
                        className={`w-4 h-4 transition-transform ${isServicesOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isServicesOpen && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <Link
                          to="/deposits"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium">{t("header.services.deposits.title")}</div>
                          <div className="text-xs text-gray-500">{t("header.services.deposits.desc")}</div>
                        </Link>
                        <Link
                          to="/insurance"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium">{t("header.services.insurance.title")}</div>
                          <div className="text-xs text-gray-500">{t("header.services.insurance.desc")}</div>
                        </Link>
                        <Link
                          to="/location"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium">{t("header.services.locations.title")}</div>
                          <div className="text-xs text-gray-500">{t("header.services.locations.desc")}</div>
                        </Link>
                        <Link
                          to="/sales-donations"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium">{t("header.services.sales.title")}</div>
                          <div className="text-xs text-gray-500">{t("header.services.sales.desc")}</div>
                        </Link>
                        <Link
                          to="/succession"
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium">{t("header.services.estate.title")}</div>
                          <div className="text-xs text-gray-500">{t("header.services.estate.desc")}</div>
                        </Link>
                        <Link
                          to="/pertes-vols"
                          onClick={() => {
                            setIsServicesOpen(false);
                            closeMobileMenu?.();
                          }}
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <div className="font-medium">{t("header.services.losses")}</div>
                          <div className="text-xs text-gray-500">{t("header.services.lossesDesc")}</div>
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <Link
                  to={proDashboardPath}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
                >
                  {t("header.nav.dashboard")}
                </Link>
              )}
            </nav>
          )}

          {/* Actions Desktop + Selecteur langue */}
          <div className="hidden lg:flex items-center space-x-3">
            <LanguageSelector />

            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin/dashboard"
                    className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t("header.admin")}
                  </Link>
                )}

<Link
  to={profilePath}
  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black hover:bg-gray-50 rounded-lg transition-all"
>
  {t("header.profile")}
</Link>

                <button
                  onClick={handleLogout}
                  className="px-5 py-2.5 text-sm font-medium bg-gray-100 text-gray-900 hover:bg-gray-200 rounded-lg transition-all"
                >
                  {t("header.logout")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="px-6 py-2.5 text-sm font-medium bg-black text-white hover:bg-gray-800 rounded-lg transition-all shadow-sm"
              >
                {t("header.login")}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={toggleMobileMenu}
          >
            {isMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu (auth only) */}
        {(isMenuOpen || isMenuClosing) && isAuthenticated && (
          <div
            className={[
              "lg:hidden pb-6 origin-top",
              "transition-all duration-200 ease-out",
              isMenuClosing ? "opacity-0 -translate-y-2 scale-[0.98]" : "opacity-100 translate-y-0 scale-100",
            ].join(" ")}
          >
            <MobileLangSelector />

            <nav className="flex flex-col space-y-1">
              {!isPro ? (
                <>
                  <Link to="/collections" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.nav.collection")}
                  </Link>
                  <Link to="/estimation" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.nav.estimates")}
                  </Link>
                  <Link to="/commencer" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.nav.start", "Commencer")}
                  </Link>
                  {hasAdvisor && (
                    <Link to="/mon-conseiller" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                      {t("header.nav.myAdvisor", "Mon conseiller")}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => { openVision(); closeMobileMenu?.(); }}
                    className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg text-left"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("header.nav.analysesIA", "Analyses IA")}
                  </button>
                  <Link to="/location" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.services.locations.title")}
                  </Link>
                  <Link to="/deposits" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.services.deposits.title")}
                  </Link>
                  <Link to="/insurance" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.services.insurance.title")}
                  </Link>
                  <Link to="/sales-donations" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.services.sales.title")}
                  </Link>
                  <Link to="/succession" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                    {t("header.services.estate.title")}
                  </Link>
                  <Link
                    to="/pertes-vols"
                    onClick={() => {
                      setIsServicesOpen(false);
                      closeMobileMenu?.();
                    }}
                    className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    {t("header.services.losses")}
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to={proDashboardPath}
                    className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    {t("header.nav.dashboard")}
                  </Link>

                  {/* ✅ Expert: collections */}
                  {proCollectionsPath && (
                    <Link
                      to={proCollectionsPath}
                      className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                    >
                      {t("header.nav.collection")}
                    </Link>
                  )}
                </>

              )}

              <div className="border-t border-gray-200 my-2" />

              {isAdmin && (
                <Link
                  to="/admin/dashboard"
                  className="px-4 py-3 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t("header.admin")}
                </Link>
              )}

              <Link to="/profile" className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
                {t("header.profile")}
              </Link>
              <button
                onClick={handleLogout}
                className="px-4 py-3 text-sm font-medium text-left text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                {t("header.logout")}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
